import { useMutation } from '@lichtblick/apollo';
import { useTranslation } from '@lichtblick/i18next';
import { AddCircleOutline } from '@lichtblick/icons';
import { logger } from '@lichtblick/logger';
import { UploadWorkOrderDocuments_V1Mutation, UploadWorkOrderDocuments_V1MutationVariables } from '@lichtblick/types';

import { UPLOAD_WORK_ORDER_DOCUMENTS } from './graphql/uploadWorkOrderDocuments';

import { Button } from '../Button';
import { MessageDialog } from '../Dialog';
import { UploadZone } from '../UploadZone/UploadZone';

export const UploadModal = ({
  folderId,
  isModalOpen,
  onSuccess,
  setIsModalOpen,
  workOrderId,
}: {
  folderId: number;
  isModalOpen: boolean;
  onSuccess?: () => void;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  workOrderId: number;
}) => {
  const { t } = useTranslation();
  const [uploadWorkOrderDocuments] = useMutation<
    UploadWorkOrderDocuments_V1Mutation,
    UploadWorkOrderDocuments_V1MutationVariables
  >(UPLOAD_WORK_ORDER_DOCUMENTS);

  const onUpload = async (fileList: File[]) => {
    const logContext = {
      component: 'WorkOrderUploader',
      operation: 'uploadWorkOrderDocuments',
      workOrderId,
      folderId,
      fileCount: fileList.length,
    };

    try {
      const response = await uploadWorkOrderDocuments({
        variables: {
          input: {
            workOrderId,
            folderId,
            files: fileList,
          },
        },
      });

      if (response.data?.uploadWorkOrderDocuments_V1.error !== 'NONE') {
        logger.warn('Upload failed with server-reported error', {
          ...logContext,
          error: response.data?.uploadWorkOrderDocuments_V1.error,
        });

        return false;
      }

      const isSuccess = !!response.data?.uploadWorkOrderDocuments_V1.data;

      if (isSuccess) {
        onSuccess?.();
      }

      return isSuccess;
    } catch (error) {
      logger.error('Unexpected error during file upload', {
        ...logContext,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      return false;
    }
  };

  return (
    <MessageDialog
      isActionContainer={false}
      isOpen={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
      }}
      title={t('pm:uploadFile')}
      trigger={<Button icon={<AddCircleOutline />} label={t('pm:addFileBtn')} onClick={() => setIsModalOpen(true)} />}
    >
      <UploadZone onUpload={onUpload} />
    </MessageDialog>
  );
};
